// A form brought onto a record read again (F-53): what the person typed since the sheet opened stays,
// every field they left alone takes the latest, so saving never undoes someone else's change. A
// group of fields held together (an address's street, city and postal code) is rebased field by
// field, so typing the suite never keeps the city as it was (QA-547).

import { RpcError } from '@flowology/client';

/** A rowVersion refusal (F-53): someone else saved the record since this copy was read. */
export const isStale = (e: unknown) =>
  e instanceof RpcError &&
  e.code === 'conflict' &&
  typeof e.detail === 'object' &&
  e.detail !== null &&
  'expected' in e.detail &&
  'current' in e.detail;

const isGroup = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** The form after a fresh read: each field typed since `was` stays, every other takes `latest`. */
export function rebase<T extends object>(was: T, typed: T, latest: T): T {
  const out = { ...latest } as Record<string, unknown>;
  const w = was as Record<string, unknown>;
  const l = latest as Record<string, unknown>;
  for (const [k, v] of Object.entries(typed)) {
    if (isGroup(v) && isGroup(w[k]) && isGroup(l[k])) out[k] = rebase(w[k], v, l[k]);
    else if (JSON.stringify(v) !== JSON.stringify(w[k])) out[k] = v;
  }
  return out as T;
}
